import React from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, Gift } from "lucide-react";

const aiProducts = [
  "AI Receptionist", "AI Hygiene Recall", "AI Case Acceptance", "AI Insurance Verification",
  "AI Claim Scrubbing", "AI Denial Management", "AI Patient Billing", "AI AR & Collections",
  "AI EOB Posting", "AI Insurance Calls", "AI Schedule Optimizer", "AI Patient Intake",
  "AI Practice Manager", "AI Patient Experience", "AI Fax Manager",
  "AI Copilot", "AI Scribe", "AI Treatment Planner", "AI Revenue Analytics", "AI Claim Submission",
];

const softwareItems = [
  // PMS Core
  "Scheduling & Appointments", "Tooth Charting", "Perio Charting",
  "Treatment Planning", "Clinical Notes", "Patient Ledgers",
  "Patient Check-In / Checkout", "Medical History", "Screenings",
  "Audit Trails", "Document Management", "Digital Imaging (SOTA)",
  // Analytics
  "Revenue Analytics Suite", "Daily Huddles Dashboard", "Collections Reporting", "Production Tracking",
  // Communication
  "VoIP Phone System", "2-Way Patient Messaging", "Next-Gen Forms",
  "Intelligent Fax", "SMS Campaigns", "Email Campaigns",
  "Physical Phones", "Online Scheduling", "Team Chat",
  // Payments
  "Text-to-Pay", "Terminal Card Readers",
  // Platform
  "eClaims Portal", "Patient Portal", "Marketing & Campaigns",
  "ePrescribing (Coming Soon)",
];

const EngagementModelsFixedSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #fbcfe8 100%)" }} />
      <div className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[140px] opacity-15 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #ddd6fe 100%)" }} />
      {/* Subtle dot grid */}
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1040px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-600 text-sm font-semibold mb-4 border border-blue-100/60 shadow-sm">
            <Sparkles className="w-4 h-4" />
            <span>One Platform, One Price</span>
          </div>
          <h2 className="text-4xl lg:text-[48px] font-bold text-slate-900 tracking-tight leading-tight">
            Unparalleled technology.{" "}
            <span className="bg-gradient-to-r from-blue-600 via-violet-500 to-blue-600 bg-clip-text text-transparent">
              Unbeatable pricing.
            </span>
          </h2>
        </motion.div>

        {/* Pricing Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="w-full rounded-[20px] overflow-hidden shadow-[0_8px_60px_-12px_rgba(0,0,0,0.08)] border border-white/80"
          style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,255,0.95) 50%, rgba(255,255,255,0.95) 100%)" }}
        >
          {/* Price Hero Section — two tiers */}
          <div className="relative px-8 pt-8 pb-6">
            {/* Subtle gradient accent line at top */}
            <div className="absolute top-0 left-0 right-0 h-[3px]"
              style={{ background: "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #3b82f6 100%)" }} />

            <div className="grid grid-cols-2 gap-6">
              {/* Platform tier */}
              <div className="flex flex-col items-center text-center border-r border-slate-100/80 pr-4">
                <p className="text-[11px] font-bold text-violet-600 uppercase tracking-wider mb-2">
                  Software Platform
                </p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-5xl font-extrabold tracking-tight bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 bg-clip-text text-transparent">
                    $16,000
                  </span>
                  <span className="text-base font-medium text-slate-400">one-time</span>
                </div>
                <p className="text-[12px] text-slate-500 mt-2 leading-snug max-w-[280px]">
                  Includes <strong className="text-slate-700">$4,000 premium onboarding</strong> & <strong className="text-slate-700">data transformation</strong> fees
                </p>
              </div>

              {/* AI package tier */}
              <div className="flex flex-col items-center text-center pl-4">
                <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2">
                  AI Package
                </p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-5xl font-extrabold tracking-tight bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 bg-clip-text text-transparent">
                    $1,200
                  </span>
                  <span className="text-base font-medium text-slate-400">/mo</span>
                </div>
                <p className="text-[12px] text-slate-500 mt-2 leading-snug max-w-[280px]">
                  All <strong className="text-slate-700">20 AI products</strong> — receptionist, billing, scheduling & more
                </p>
              </div>
            </div>
          </div>

          {/* Bundle & Save callout */}
          <div className="mx-8 mb-5">
            <div className="flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-xl border border-emerald-200/70 bg-gradient-to-r from-emerald-50/80 via-blue-50/60 to-violet-50/70">
              <Gift className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="text-[12.5px] text-slate-700 font-medium text-center">
                <strong className="text-emerald-700">Bundle & save:</strong> sign up for both the platform and the AI package and we{" "}
                <strong className="text-slate-900">waive the $4,000 onboarding fee</strong> + data transformation fees.
              </span>
            </div>
          </div>

          {/* Divider */}
          <div className="mx-8 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

          {/* Product Lists */}
          <div className="grid grid-cols-2">
            <div className="px-8 pt-5 pb-7 border-r border-slate-100/80">
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className="w-5 h-5 rounded-md bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-[10px] font-bold shadow-sm">20</span>
                AI products included
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {aiProducts.map((product, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                    <span className="text-[12px] text-slate-600">{product}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="px-8 pt-5 pb-7">
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <span className="w-5 h-5 rounded-md bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center text-white text-[10px] font-bold shadow-sm">10</span>
                Softwares included
              </p>
              <div className="grid grid-cols-3 gap-x-3 gap-y-1.5">
                {softwareItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Check className="w-3 h-3 text-violet-500 shrink-0" />
                    <span className="text-[11px] text-slate-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom bar — credits disclaimer */}
          <div className="border-t border-slate-100/80 px-8 py-3 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-500" />
              <span className="text-[12px] font-medium text-slate-500">
                <strong className="text-slate-700">150,000 AI credits</strong> included &middot; overage at <strong className="text-slate-700">2.3 cents</strong> per 1,000 AI credits
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EngagementModelsFixedSlide;
